import { useEffect, useState, type CSSProperties } from "react";
import { trackEvent } from "../lib/analytics";
import type { SupportedLocale } from "../lib/localization";
import { CMS_PUBLIC_PATH } from "../lib/publicHome";
import {
  BLOG_PATH,
  blogPostHref,
  CONTACT_PATH,
  formatPostDate,
  isStaleWebsiteCopy,
  localizedText,
  mergeSection,
  resolveNavItems,
  resolveSiteHref,
  resolvePublicTheme,
  SOLUTIONS_PATH,
  themeStyle,
  visibleBlogPosts,
  WEBSITE_DEFAULTS,
  withSolutionsNav,
  type AudienceItem,
  type BlogItem,
  type FeatureItem,
  type FooterLink,
  type LogoItem,
  type NavLink,
  type SolutionLine,
  type StatItem,
  type TestimonialItem,
  type WorkItem,
} from "../lib/websiteDefaults";
import { SiteAudiences } from "./public/SiteAudiences";
import { SiteBlog } from "./public/SiteBlog";
import { BlogIndexPage, BlogPostPage, type BlogCardPost } from "./public/SiteBlogPages";
import { SiteContact } from "./public/SiteContact";
import { SiteContactPage } from "./public/SiteContactPage";
import { SiteFooter } from "./public/SiteFooter";
import { SiteCta } from "./public/SiteCta";
import { BlueprintMark, SiteHero } from "./public/SiteHero";
import { SiteMobileMenu, SiteNav } from "./public/SiteNav";
import { SiteServices } from "./public/SiteServices";
import { SiteStats } from "./public/SiteStats";
import { SiteTestimonials } from "./public/SiteTestimonials";
import { SiteWork } from "./public/SiteWork";
import { SiteSolutions } from "./public/SiteSolutions";
import { SolutionDetailPage, SolutionsIndexPage, type SolutionCard } from "./public/SiteSolutionsPages";
import { solutionHref, visibleSolutions, type SolutionItem } from "../lib/solutions";

const DEFAULT_SOCIAL = {
  facebook: WEBSITE_DEFAULTS.footer.facebook,
  instagram: WEBSITE_DEFAULTS.footer.instagram,
  linkedin: WEBSITE_DEFAULTS.footer.linkedin,
};

/** Empty CMS lists mean "not configured yet", so the Framer defaults stay visible. */
function listOr<T>(value: unknown, fallback: readonly T[]): T[] {
  return Array.isArray(value) && value.length ? (value as T[]) : [...fallback];
}

function pickSocialUrl(links: unknown, names: string[], fallback: string) {
  if (!Array.isArray(links)) return fallback;
  const match = links.find((link) => {
    const name = String(typeof link === "object" && link ? (link as { name?: string }).name || "" : link).toLowerCase();
    const url = String(typeof link === "object" && link ? (link as { url?: string }).url || "" : "");
    return names.some((key) => name.includes(key) || url.includes(key));
  });
  if (match && typeof match === "object" && match && "url" in match && (match as { url?: string }).url) {
    return String((match as { url: string }).url);
  }
  return fallback;
}

/** Which public page to render: the home page, the contact page, the blog list, or one post. */
export type PublicPage =
  | { type: "home" }
  | { type: "contact" }
  | { type: "blog" }
  | { type: "post"; slug: string }
  | { type: "solutions" }
  | { type: "solution"; slug: string };

interface WebsitePreviewProps {
  previewData?: Record<string, any>;
  locale?: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
  page?: PublicPage;
}

export function WebsitePreview({ previewData, locale = "en", onLocaleChange, page = { type: "home" } }: WebsitePreviewProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const data = previewData || {};
  const stale = isStaleWebsiteCopy(data);
  const section = <K extends Parameters<typeof mergeSection>[0]>(key: K) =>
    mergeSection(key, stale ? null : data?.[key]?.content);
  const header = section("header");
  const hero = section("hero");
  const audiencesSection = section("audiences");
  const stats = section("stats");
  const work = section("work");
  const services = section("services");
  const cta = section("cta");
  const testimonials = section("testimonials");
  const blog = section("blog");
  const contact = section("contact");
  const solutions = section("solutions");
  const footer = section("footer");
  const theme = resolvePublicTheme(stale ? null : data?.theme?.content);
  const tr = (en: string, es: string) => (locale === "es" ? es : en);
  const text = (value: unknown, fallback = "") => localizedText(value, locale, fallback);

  const showCta = (header as { showCTA?: unknown }).showCTA !== false;
  const navItems = withSolutionsNav(
    resolveNavItems(header.navigation as NavLink[] | string[], locale),
    locale,
    (solutions as { showInNav?: unknown }).showInNav !== false
  );
  const primaryCtaHref = text(header.ctaUrl, "#contact") || "#contact";

  const scrollTo = (rawHref: string) => {
    // "#contact" opens the contact page, as on Framer; on that page it scrolls to the form.
    const href = page.type === "contact" && (rawHref === "#contact" || rawHref === CONTACT_PATH) ? "#contact" : resolveSiteHref(rawHref);
    if (!href.startsWith("#")) {
      window.location.href = href;
      return;
    }
    const element = document.getElementById(href.slice(1));
    if (!element) {
      // Section anchors on the other pages point back to the home page.
      if (page.type !== "home") window.location.href = `${CMS_PUBLIC_PATH}${href}`;
      return;
    }
    const top = href === "#hero" ? 0 : element.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const onCta = (href: string, placement: string) => {
    trackEvent("website_cta_click", { placement, locale, href });
    if (href.startsWith("#")) {
      scrollTo(href);
    }
  };

  const onNavigate = (href: string) => {
    setMobileMenuOpen(false);
    if (href === primaryCtaHref) {
      trackEvent("website_cta_click", { placement: "header", locale, href });
    }
    if (href.startsWith("#") || href === CONTACT_PATH) {
      setTimeout(() => scrollTo(href), 40);
      return;
    }
    window.location.href = href;
  };

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const audiences = (Array.isArray(audiencesSection.items) ? audiencesSection.items : []) as AudienceItem[];
  const serviceItems = (Array.isArray(services.items) ? services.items : []) as Array<{
    name: unknown;
    description?: unknown;
    image?: string;
  }>;
  const features = (Array.isArray((services as { features?: FeatureItem[] }).features)
    ? (services as { features: FeatureItem[] }).features
    : []) as FeatureItem[];
  const workItems = listOr<WorkItem>(work.items, WEBSITE_DEFAULTS.work.items).filter(
    (item) => text(item.title) || item.image
  );
  const logos = listOr<LogoItem>((stats as { logos?: unknown }).logos, WEBSITE_DEFAULTS.stats.logos);
  const stringOr = (value: unknown) => (typeof value === "string" && value ? value : undefined);
  const stringList = (value: unknown) =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && Boolean(item)) : [];
  const footerColumns = listOr<{ title: unknown; links: FooterLink[] }>(
    (footer as { columns?: unknown }).columns,
    WEBSITE_DEFAULTS.footer.columns
  ).map((column) => ({
    title: text(column.title),
    links: (Array.isArray(column.links) ? column.links : []).map((link) => ({
      label: text(link.label),
      href: resolveSiteHref(link.href),
      soon: link.soon,
    })),
  }));
  const testimonialItems = (Array.isArray(testimonials.items) ? testimonials.items : []).filter(
    (item: TestimonialItem) => text(item.quote) || item.name
  ) as TestimonialItem[];
  const statItems = (Array.isArray(stats.items) ? stats.items : []) as StatItem[];
  const blogPosts: Array<BlogCardPost & { item: BlogItem }> = visibleBlogPosts(blog.items)
    .filter((item) => text(item.title))
    .map((item) => ({
      item,
      href: blogPostHref(item),
      category: text(item.category),
      title: text(item.title),
      excerpt: text(item.excerpt),
      author: item.author || text((blog as { author?: unknown }).author),
      date: formatPostDate(item, locale),
      image: item.image,
    }));
  const statsSection = (
    <SiteStats
      label={text(stats.label)}
      avatars={stringList((stats as { avatars?: unknown }).avatars)}
      logos={logos}
      items={statItems.map((item) => ({ value: item.value, label: text(item.label) }))}
    />
  );
  const socials = [
    {
      name: "LinkedIn",
      href: String(footer.linkedin || pickSocialUrl(footer.socialLinks, ["linkedin"], DEFAULT_SOCIAL.linkedin)),
    },
    {
      name: "Facebook",
      href: String(footer.facebook || pickSocialUrl(footer.socialLinks, ["facebook"], DEFAULT_SOCIAL.facebook)),
    },
    {
      name: "Instagram",
      href: String(footer.instagram || pickSocialUrl(footer.socialLinks, ["instagram"], DEFAULT_SOCIAL.instagram)),
    },
  ];
  const solutionItems = visibleSolutions(solutions.items);
  const solutionCards: Array<SolutionCard & { item: SolutionItem }> = solutionItems.map((item, index) => ({
    item,
    key: item.slug || `${item.name}-${index}`,
    name: item.name,
    href: solutionHref(item),
    kind: text(item.kind),
    category: typeof item.category === "string" ? item.category : "",
    summary: text(item.summary),
    icon: item.icon,
    image: item.image,
    tags: (Array.isArray(item.tags) ? item.tags : []).map((tag) => text(tag)).filter(Boolean),
    available: item.status !== "soon",
  }));
  const featuredSolution = solutionCards.find((card) => card.item.featured) || solutionCards[0];
  const solutionLines = (Array.isArray((solutions as { lines?: unknown }).lines)
    ? ((solutions as { lines: SolutionLine[] }).lines)
    : []
  ).map((line) => ({
    title: text(line.title),
    description: text(line.description),
    linkLabel: text(line.linkLabel),
    href: line.href || SOLUTIONS_PATH,
    icon: line.icon,
  }));
  const sText = (key: string, fallback = "") => text((solutions as Record<string, unknown>)[key], fallback);
  const currentSolution = page.type === "solution" ? solutionItems.find((item) => item.slug === page.slug) : undefined;
  const currentPost = page.type === "post" ? blogPosts.find((post) => post.item.slug === page.slug) : undefined;

  // Opening /site#services from a blog page: scroll once the home sections exist.
  useEffect(() => {
    if (page.type !== "home" || !window.location.hash) return;
    const timer = window.setTimeout(() => scrollTo(window.location.hash), 120);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.type]);

  return (
    <div className="public-site min-h-screen" style={themeStyle(theme) as CSSProperties}>
      <SiteNav
        logo={typeof header.logo === "string" ? header.logo : undefined}
        items={navItems}
        ctaLabel={text(header.ctaText, tr("Let's talk", "Hablemos"))}
        ctaHref={primaryCtaHref}
        showCta={Boolean(showCta)}
        locale={locale}
        onLocaleChange={onLocaleChange}
        menuOpen={mobileMenuOpen}
        onToggleMenu={() => setMobileMenuOpen((open) => !open)}
        onNavigate={onNavigate}
      />
      {mobileMenuOpen ? (
        <SiteMobileMenu
          items={navItems}
          ctaLabel={text(header.ctaText, tr("Let's talk", "Hablemos"))}
          ctaHref={primaryCtaHref}
          showCta={Boolean(showCta)}
          locale={locale}
          onLocaleChange={onLocaleChange}
          onNavigate={onNavigate}
        />
      ) : null}

      {page.type === "home" ? (
        <>
          <SiteHero
            badge={text(hero.badge)}
            badgeAvatars={stringList((hero as { badgeAvatars?: unknown }).badgeAvatars)}
            title={text(hero.title)}
            description={text(hero.description)}
            primaryLabel={text(hero.ctaText)}
            primaryHref={text(hero.ctaUrl, "#contact")}
            secondaryLabel={tr("See services", "Ver servicios")}
            secondaryHref="#services"
            locale={locale}
            onCta={onCta}
          >
            <SiteAudiences
              items={audiences.map((group) => ({
                name: text(group.name),
                description: text(group.description),
                image: group.image,
              }))}
            />
          </SiteHero>

          {statsSection}

          <div className="relative isolate">
          <BlueprintMark kind="angle" className="right-[3%] top-[40px] h-[140px] w-[140px]" />
          <BlueprintMark kind="cross" className="bottom-[60px] left-[2%] hidden h-6 w-6 min-[810px]:block" />
          <SiteServices
            badge={text(services.badge)}
            title={text(services.title)}
            description={text(services.description)}
            items={serviceItems.map((item) => ({
              name: text(item.name),
              description: text(item.description),
              image: item.image,
            }))}
            featuresTitle={text((services as { featuresTitle?: unknown }).featuresTitle)}
            features={features.map((feature) => ({
              title: text(feature.title),
              description: text(feature.description),
              icon: feature.icon,
            }))}
          />
          </div>

          <SiteCta
            title={text(cta.title)}
            ctaLabel={text(cta.ctaText)}
            ctaHref={text(cta.ctaUrl, "#contact")}
            onCta={() => onCta(text(cta.ctaUrl, "#contact"), "mid-cta")}
          />

          {(solutions as { showOnHome?: unknown }).showOnHome !== false ? (
            <SiteSolutions
              badge={sText("badge")}
              title={sText("title")}
              description={sText("description")}
              featured={
                featuredSolution
                  ? {
                      name: featuredSolution.name,
                      href: featuredSolution.href,
                      summary: text(featuredSolution.item.description) || featuredSolution.summary,
                      icon: featuredSolution.icon,
                      image: featuredSolution.image,
                      tags: featuredSolution.tags,
                      statusLabel: featuredSolution.available ? sText("availableLabel") : sText("soonLabel"),
                      appStoreUrl: featuredSolution.item.appStoreUrl,
                    }
                  : undefined
              }
              featuredCta={sText("featuredCta")}
              appStoreLabel={sText("appStoreLabel", "App Store")}
              lines={solutionLines}
              ctaLabel={sText("ctaText")}
              ctaHref={SOLUTIONS_PATH}
              onNavigate={onNavigate}
            />
          ) : null}

          <SiteWork
            badge={text((work as { badge?: unknown }).badge)}
            title={text(work.title)}
            description={text((work as { description?: unknown }).description)}
            items={workItems.map((item) => ({
              title: text(item.title),
              meta: [text(item.discipline, ""), item.year || ""].filter(Boolean).join(" · "),
              image: item.image,
            }))}
          />

          <div className="relative isolate">
          <BlueprintMark kind="dimension" className="left-[4%] top-[48px] hidden h-[30px] w-[260px] min-[810px]:block" />
          <BlueprintMark kind="diagonal" className="right-[6%] top-[24px] hidden h-[200px] w-[200px] min-[810px]:block" />
          <SiteTestimonials
            badge={text(testimonials.badge)}
            title={text(testimonials.title)}
            description={text((testimonials as { description?: unknown }).description)}
            items={testimonialItems.map((item) => ({
              quote: text(item.quote),
              name: item.name || "",
              role: text(item.role),
              rating: item.rating || "",
              avatar: item.avatar,
            }))}
          />
          <BlueprintMark kind="cross" className="bottom-0 right-[2%] h-6 w-6" />
          </div>

          <SiteBlog
            badge={text(blog.badge)}
            title={text(blog.title)}
            ctaLabel={text(blog.ctaLabel)}
            ctaHref={resolveSiteHref(text(blog.ctaUrl, BLOG_PATH))}
            author={text((blog as { author?: unknown }).author)}
            items={blogPosts.slice(0, 4)}
          />
        </>
      ) : null}

      {page.type === "contact" ? (
        <>
          <SiteContactPage
            title={text((contact as { pageTitle?: unknown }).pageTitle)}
            description={text((contact as { pageDescription?: unknown }).pageDescription)}
            image={stringOr((contact as { pageImage?: unknown }).pageImage)}
            general={{
              title: text((contact as { generalTitle?: unknown }).generalTitle),
              text: text((contact as { generalText?: unknown }).generalText),
              email: text(contact.email),
            }}
            support={{
              title: text((contact as { supportTitle?: unknown }).supportTitle),
              text: text((contact as { supportText?: unknown }).supportText),
              email: text((contact as { supportEmail?: unknown }).supportEmail),
            }}
            socialTitle={text((contact as { socialTitle?: unknown }).socialTitle)}
            socials={socials}
            serviceOptions={listOr<unknown>((contact as { serviceOptions?: unknown }).serviceOptions, WEBSITE_DEFAULTS.contact.serviceOptions)
              .map((option) => text(option))
              .filter(Boolean)}
            submitLabel={text((contact as { submitText?: unknown }).submitText, tr("Send Message", "Enviar mensaje"))}
            locale={locale}
          />
          <div className="pt-20 min-[810px]:pt-[160px]">{statsSection}</div>
        </>
      ) : null}

      {page.type === "blog" ? (
        <BlogIndexPage
          badge={text(blog.badge)}
          title={text((blog as { pageTitle?: unknown }).pageTitle)}
          description={text((blog as { pageDescription?: unknown }).pageDescription)}
          image={stringOr((blog as { pageImage?: unknown }).pageImage)}
          posts={blogPosts}
          promo={{
            title: text((blog as { promoTitle?: unknown }).promoTitle),
            text: text((blog as { promoText?: unknown }).promoText),
            cta: text((blog as { promoCta?: unknown }).promoCta),
          }}
          onCta={onCta}
        />
      ) : null}

      {page.type === "post" && currentPost ? (
        <BlogPostPage
          post={{ ...currentPost, date: formatPostDate(currentPost.item, locale, true) }}
          body={text(currentPost.item.body)}
          related={blogPosts.filter((post) => post !== currentPost).slice(0, 3)}
          labels={{
            home: tr("Start", "Inicio"),
            blog: "Blog",
            related: text((blog as { relatedTitle?: unknown }).relatedTitle, tr("Related articles", "Artículos relacionados")),
            viewAll: text(blog.ctaLabel),
          }}
          homeHref={CMS_PUBLIC_PATH}
          blogHref={BLOG_PATH}
          onCta={onCta}
        />
      ) : null}

      {page.type === "post" && !currentPost ? (
        <section id="hero" className="site-container flex min-h-[60vh] flex-col items-center justify-center pt-32 text-center">
          <h1 className="site-h2 text-white">{tr("Post not found", "Artículo no encontrado")}</h1>
          <a href={BLOG_PATH} className="site-btn mt-8">
            {text(blog.ctaLabel)}
          </a>
        </section>
      ) : null}

      {page.type === "solutions" ? (
        <SolutionsIndexPage
          badge={sText("pageBadge")}
          title={sText("pageTitle")}
          description={sText("pageDescription")}
          solutions={solutionCards}
          labels={{
            all: tr("All", "Todas"),
            categories: {
              apps: "Apps",
              ai: tr("Artificial intelligence", "Inteligencia artificial"),
              software: tr("Custom software", "Software a medida"),
              integrations: tr("Integrations", "Integraciones"),
            },
            details: sText("detailsLabel"),
            available: sText("availableLabel"),
            soon: sText("soonLabel"),
          }}
          soonCard={
            (solutions as { showSoonCard?: unknown }).showSoonCard !== false
              ? { kind: sText("soonKind"), title: sText("soonTitle"), text: sText("soonText") }
              : undefined
          }
          ideaCard={{ kind: sText("ideaKind"), title: sText("ideaTitle"), text: sText("ideaText"), cta: sText("ideaCta") }}
          onCta={onCta}
        />
      ) : null}

      {page.type === "solution" && currentSolution ? (
        <SolutionDetailPage
          locale={locale}
          solution={{
            name: currentSolution.name,
            kind: text(currentSolution.kind),
            byLabel: sText("byLabel"),
            headline: text(currentSolution.headline),
            description: text(currentSolution.description) || text(currentSolution.summary),
            icon: currentSolution.icon,
            screenshots: stringList(currentSolution.screenshots),
            platform: text(currentSolution.platform),
            appStoreUrl: currentSolution.appStoreUrl,
            features: (Array.isArray(currentSolution.features) ? currentSolution.features : []).map((feature) => ({
              title: text(feature.title),
              description: text(feature.description),
              icon: feature.icon,
            })),
            dataBadge: sText("dataBadge"),
            dataTitle: text(currentSolution.dataTitle),
            dataPoints: (Array.isArray(currentSolution.dataPoints) ? currentSolution.dataPoints : [])
              .map((point) => text(point))
              .filter(Boolean),
            links: [
              currentSolution.privacyUrl ? { label: sText("privacyLabel"), href: currentSolution.privacyUrl, primary: true } : null,
              currentSolution.termsUrl ? { label: sText("termsLabel"), href: currentSolution.termsUrl } : null,
              currentSolution.supportUrl ? { label: sText("supportLabel"), href: currentSolution.supportUrl } : null,
            ].filter((link): link is { label: string; href: string; primary?: boolean } => Boolean(link)),
          }}
        />
      ) : null}

      {page.type === "solution" && !currentSolution ? (
        <section id="hero" className="site-container flex min-h-[60vh] flex-col items-center justify-center pt-32 text-center">
          <h1 className="site-h2 text-white">{tr("Solution not found", "Solución no encontrada")}</h1>
          <a href={SOLUTIONS_PATH} className="site-btn mt-8">
            {sText("ctaText")}
          </a>
        </section>
      ) : null}

      <SiteContact
        badge={text((contact as { badge?: unknown }).badge)}
        title={text(contact.title)}
        description={text(contact.description)}
        ctaLabel={text((contact as { ctaText?: unknown }).ctaText, tr("Get a Quote", "Pide tu cotización"))}
        href={page.type === "contact" ? "#contact" : CONTACT_PATH}
        onCta={() => onCta("#contact", "estimate-box")}
      />

      <SiteFooter
        logo={typeof footer.logo === "string" ? footer.logo : undefined}
        tagline={text((footer as { tagline?: unknown }).tagline)}
        copyright={text(footer.text, "© 2026 ON45D Studio LLC. All rights reserved.")}
        socials={socials}
        columns={footerColumns}
        newsletter={{
          title: text((footer as { newsletterTitle?: unknown }).newsletterTitle, "Get notified"),
          text: text((footer as { newsletterText?: unknown }).newsletterText),
          label: text((footer as { newsletterLabel?: unknown }).newsletterLabel),
          button: text((footer as { newsletterButton?: unknown }).newsletterButton),
        }}
        locale={locale}
        onLocaleChange={onLocaleChange}
        onNavigate={onNavigate}
      />
    </div>
  );
}
